import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DropdownOption, SearchOption } from 'globals/store/programs.data';
import { transpose } from '../../helpers/helpers';
import { Dictionary } from '@ngrx/entity';
import { fuzzySearch } from '../../helpers/text-search';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DropdownComponent } from '../misc/dropdown.component';
import { BigDialogWrapperComponent } from '../wrappers/big-dialog-wrapper.component';

@Component({
    selector: 'fuzzy-import-dialog',
    template: `
<wide-dialog-wrapper
  [class]="'padded'"
  [successLabel]="'IMPORT'"
  [delete]="false"
  [dialogTitle]="data.dialogTitle"
  [backgroundColor]="'#41474f'"
  [whiteTitle]="true"
  [midLeftLabel]="'Close'"
  [disabled]="!usedColumns.size"
  (onMidLeft)="dialogRef.close(null)"
  (onSubmit)="submit()"
>
  <div class="import_dialog_wrapper">
    <div class="import_dialog_content">
      <div *ngIf="!data.sheet">
        <h3>Spreadsheet import error</h3>
        <div>
          Something went wrong while importing your data. Please try a different .xlsx, .xls or .csv file.
        </div>
      </div>
      <div *ngIf="data.sheet">
        <h3>Match imported properties to spreadsheet columns</h3>
        <div>
          <mat-checkbox class="pad-right" color="primary" [checked]="hasHeadingsRow" (change)="toggleHeadRow()">First row contains headings</mat-checkbox>
        </div>
        <div class="dropdowns">
          <div class="dropdown" *ngFor="let field of data.columns; index as h;">
            <div class="note placeholders">{{ field.title }}</div>
            <dropdown 
              [selections]="choices"
              [null_text]="'Choose ' + field.title + ' column'" 
              [selected]="choices[selectedOptions[field.id]]" 
              (select)="setField(field.id, $event.id)"
            ></dropdown>
          </div>
        </div>
        <h3>Imported spreadsheet preview</h3>
        <div class="unused">Unused columns are in faded text.</div>
        <div class="spreadsheet">
          <div class="column" [ngClass]="{ unused: !usedColumns.has(c) }" *ngFor="let column of preview; index as c" class="column" >
            <div class="cell" 
              *ngFor="let cell of column; index as r" 
              [ngClass]="{ header: r === 0, empty: cell === undefined }"
            > {{ cell }} </div>
          </div>
          <div class="column last">
            <div class="cell" *ngFor="let cell of preview[0]; index as row_index"  [ngClass]="{ header:row_index===0 }"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</wide-dialog-wrapper>
  `,
    styleUrls: ['import.dialog.scss'],
    standalone: true,
    imports: [CommonModule, MatCheckboxModule, DropdownComponent, BigDialogWrapperComponent]
})


export class FuzzyImportDialogComponent<T>  {

  hasHeadingsRow = true
  usedColumns: Set<number> = new Set()
  selectedOptions: any = {}
  choices: DropdownOption<number>[] = []
  choicesById: { [K in keyof T]?: DropdownOption<number>[] } = {}
  preview: string[][]

  constructor(
    public dialogRef: MatDialogRef<FuzzyImportDialogComponent<T>>,
    @Inject(MAT_DIALOG_DATA) public data: FuzzyImportDialogData<T>,
  ) { 
    this.setData()
    const firstRow = data.sheet[0]
    const firstRowIndexes = {}
    firstRow.forEach((column,i) => firstRowIndexes[column] = i)
    const matches = []
    const searchTermsToIds = {}
    // first search for object properties in first row
    data.columns.forEach(({ id, title, searchTerms }) => { 
      searchTermsToIds[title] = id
      searchTerms.forEach(term => searchTermsToIds[title] = id)
      const allTerms = [ title, ...searchTerms ]
      allTerms.forEach(searchTerm => {
        const results = fuzzySearch(searchTerm, firstRow)
        results.forEach(({ target, score }) => matches.push({ id, match: firstRowIndexes[target], score })) 
      })
    })

    // then search for first row columns in object property names
    const searchTerms = Object.keys(searchTermsToIds)
    firstRow.forEach(column => {
      const results = fuzzySearch(column, searchTerms)
      results.forEach(({ target, score }) => matches.push({ id: searchTermsToIds[target], match: firstRowIndexes[column], score }))
    })
    matches.sort((a,b) => b.score - a.score)
    const usedIndexes= new Set()
    data.columns.forEach(({ id }) => {
      const match = matches.find(m => m.id === id && !usedIndexes.has(m.match))
      if (match) {
        this.setField(id, match.match)
        usedIndexes.add(match.match)
      }
    })
  }

  setField(fieldId: any, dataIndex: number) {
    this.selectedOptions[fieldId] = dataIndex
    this.usedColumns = new Set(Object.values(this.selectedOptions))
  }

  toggleHeadRow() {
    this.hasHeadingsRow = !this.hasHeadingsRow
    this.setData()
  }

  setData() {
    this.choices = this.data.sheet[0].map((heading, i) => ({ id: i, title: this.hasHeadingsRow ? heading : `Column ${i + 1}` }))
    const offset = this.hasHeadingsRow ? 1 : 0

    this.preview = transpose([this.choices.map(c => c.title), ...this.data.sheet.slice(0 + offset,5 + offset)])
  }

  submit() {
    const offset = this.hasHeadingsRow ? 1 : 0
    const newEntities: Partial<T>[] = this.data.sheet.slice(offset).map(r => {
      const newEntity: Partial<T> = {}
      Object.entries(this.selectedOptions).forEach(([ key, index ]) => newEntity[key] = this.data.transformers[key] ? 
        this.data.transformers[key](r[index as number]) : r[index as number])
      return newEntity
    })
    this.dialogRef.close(newEntities)
  }
}


export interface FuzzyImportDialogData<T> {
  dialogTitle: string
  columns: SearchOption<keyof T>[]
  sheet: string[][]
  transformers: { [K in keyof T]?: Function }
}
